import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/TimberStorage';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const TIMBER_STORAGE_API_REDUCER_KEY = 'timber-storages';
export const TIMBER_STORAGE_PATH = 'timber-storages';

export const timberStorageApi = createApi({
    reducerPath: TIMBER_STORAGE_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['TimberStorage', 'TimberStorages'],
    endpoints: (builder) => ({
        getTimberStorageList: builder.query<PaginationResult<Entity.TimberStorage>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ TIMBER_STORAGE_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['TimberStorages'],
        }),
        getTimberStorage: builder.query<Entity.TimberStorage, Entity.PrimaryTimberStorage>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = timberStorageNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ TIMBER_STORAGE_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['TimberStorage'],
        }),
        getTimberStorageMultiple: builder.query<Entity.TimberStorage, Entity.PrimaryTimberStorage[]>({
            query: (ids) => {
                return ({
                    url: `/${ TIMBER_STORAGE_PATH }/ids${Entity.PrimaryTimberStorage.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['TimberStorages'],
        }),
        getTimberStoragePresentation: builder.query<PaginationResult<Entity.TimberStorage>, Entity.PresentationTimberStorageRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ TIMBER_STORAGE_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['TimberStorages'],
        }),
        getTimberStorageAdvancedPresentation: builder.query<PaginationResult<Entity.TimberStorage>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ TIMBER_STORAGE_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['TimberStorages'],
        }),
        
        insertTimberStorage: builder.mutation<Entity.TimberStorage, Entity.CreateTimberStorage>({
            query: (toInsert) => {
                return ({
                    url: `/${ TIMBER_STORAGE_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    timberStorageApi.util.upsertQueryData('getTimberStorage', Entity.PrimaryTimberStorage.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['TimberStorages'],
        }),
        insertMultipleTimberStorage: builder.mutation<Entity.TimberStorage[], Entity.CreateTimberStorage[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ TIMBER_STORAGE_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            timberStorageApi.util.upsertQueryData('getTimberStorage', Entity.PrimaryTimberStorage.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['TimberStorages'],
        }),
        updateTimberStorage: builder.mutation<Entity.TimberStorage, Entity.UpdateEntityTimberStorage>({
            query: (toUpdate) => {
                return ({
                    url: `/${ TIMBER_STORAGE_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['TimberStorages'],
        }),
        updateMultipleTimberStorage: builder.mutation<Entity.TimberStorage[], Entity.UpdateEntityTimberStorage[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ TIMBER_STORAGE_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['TimberStorages'],
        }),
        deleteTimberStorage: builder.mutation<Entity.TimberStorage, Entity.TimberStorage>({
            query: (toDelete) => {
                return ({
                    url: `/${ TIMBER_STORAGE_PATH }/${Entity.PrimaryTimberStorage.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['TimberStorages'],
        }),
        deleteMultipleTimberStorage: builder.mutation<Entity.TimberStorage[], Entity.PrimaryTimberStorage[]>({
            query: (ids) => {
                return ({
                    url: `/${ TIMBER_STORAGE_PATH }/batch${Entity.PrimaryTimberStorage.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['TimberStorages'],
        }),
    }),
})

export const [timberStorageNormalizer, timberStorageNormalizerMiddleware] = createNormalizationMiddleware(TIMBER_STORAGE_API_REDUCER_KEY, timberStorageApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetTimberStorageListQuery,
    useGetTimberStorageMultipleQuery,
    useGetTimberStoragePresentationQuery,
    useGetTimberStorageQuery,
    useInsertTimberStorageMutation,
    useInsertMultipleTimberStorageMutation,
    useUpdateTimberStorageMutation,
    useUpdateMultipleTimberStorageMutation,
    useDeleteTimberStorageMutation,
    useDeleteMultipleTimberStorageMutation,
    useGetTimberStorageAdvancedPresentationQuery,
    
} = timberStorageApi
